import { useEffect, useMemo } from 'react';
import { hasGlobalTerritoriesApplied } from '@helpers/territories';
import I18n, { translate } from '@utils/i18n';
import FilterTypes from '../../../../constants/FilterTypes.ts';
import { actionTypes } from '../reducer';

const namespace = 'features.proposals.filters';

const isFilterValuePopulated = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'string' && value !== '';
};

const useFilters = ({
  filters,
  dispatch,
  statusesForSelect,
  usersForSelect,
  techniciansForSelect,
  proposalTypesForSelect,
  buildingId,
  accountId,
  inspectionId
}) => {
  const globalTerritoriesApplied = hasGlobalTerritoriesApplied();
  const hasPrimaryTerritoryScope = globalTerritoriesApplied || isFilterValuePopulated(filters.values.territory);

  useEffect(() => {
    if (!hasPrimaryTerritoryScope && isFilterValuePopulated(filters.values.additionalTerritory)) {
      dispatch({ type: actionTypes.FILTER_CHANGED, value: '', field: 'additionalTerritory' });
    }
  }, [hasPrimaryTerritoryScope, filters.values.additionalTerritory, dispatch]);

  const onChangeFilter = (fieldName) => (value) => {
    dispatch({ type: actionTypes.FILTER_CHANGED, value, field: fieldName });
  };

  const onToggleFilter = (fieldName) => () => {
    dispatch({ type: actionTypes.FILTER_TOGGLED, field: fieldName });
  };

  const onChangeDateFilter = (fieldName) => (dateFieldName, value) => {
    dispatch({
      type: actionTypes.DATE_RANGE_FILTER_CHANGED,
      field: fieldName,
      dateField: dateFieldName,
      value
    });
  };

  const filtersList = useMemo(() => {
    const list = [
      {
        field: 'status',
        locale: 'status',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.status,
        value: filters.values.status,
        onChange: onChangeFilter('status'),
        onToggle: onToggleFilter('status'),
        optionsForSelect: statusesForSelect
      },
      {
        field: 'createdBy',
        locale: 'created_by',
        type: FilterTypes.GroupedMultiSelect,
        active: filters.active.createdBy,
        value: filters.values.createdBy,
        onChange: onChangeFilter('createdBy'),
        onToggle: onToggleFilter('createdBy'),
        groupedOptions: [
          {
            label: translate('office_users', { namespace }),
            placeholder: translate('search_office_users', { namespace }),
            optionsForSelect: usersForSelect.map((user) => ({
              ...user,
              value: `user-${user.value}`
            }))
          },
          {
            label: translate('technicians', { namespace }),
            placeholder: translate('search_technicians', { namespace }),
            allowLetterAvatar: true,
            optionsForSelect: techniciansForSelect.map((technician) => ({
              ...technician,
              title: technician.label,
              value: `technician-${technician.id}`
            }))
          }
        ]
      },
      {
        field: 'createdAt',
        locale: 'date',
        type: 'dateRangePicker',
        active: filters.active.createdAt,
        values: filters.values.createdAt,
        onChange: onChangeDateFilter('createdAt'),
        onToggle: onToggleFilter('createdAt')
      },
      {
        field: 'building',
        locale: 'building',
        type: buildingId || inspectionId ? 'hidden' : FilterTypes.GenericMultiSelect,
        active: filters.active.building,
        value: filters.values.building,
        onChange: onChangeFilter('building'),
        onToggle: onToggleFilter('building')
      },
      {
        field: 'number',
        locale: 'proposal_number',
        type: 'textBox',
        active: filters.active.number,
        value: filters.values.number,
        onChange: onChangeFilter('number'),
        onToggle: onToggleFilter('number')
      },
      {
        field: 'proposalType',
        locale: 'proposal_type',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.proposalType,
        value: filters.values.proposalType,
        onChange: onChangeFilter('proposalType'),
        onToggle: onToggleFilter('proposalType'),
        optionsForSelect: proposalTypesForSelect
      },
      {
        field: 'technician',
        locale: 'technician',
        type: FilterTypes.GenericMultiSelect,
        active: filters.active.technician,
        value: filters.values.technician,
        disableSorting: true,
        onChange: onChangeFilter('technician'),
        onToggle: onToggleFilter('technician'),
        withUnassigned: false,
        allowLetterAvatar: true
      },
      {
        field: 'buildingStatus',
        locale: 'building_status',
        type: buildingId || inspectionId ? 'hidden' : 'booleanSelect',
        trueLabel: I18n.t('generic.active'),
        falseLabel: I18n.t('generic.inactive'),
        active: filters.active.buildingStatus,
        value: filters.values.buildingStatus,
        onChange: onChangeFilter('buildingStatus'),
        onToggle: onToggleFilter('buildingStatus')
      },
      {
        field: 'account',
        locale: 'account',
        type: 'hidden',
        active: !!accountId,
        value: filters.values.account,
        onChange: onChangeFilter('account'),
        onToggle: onToggleFilter('account')
      }
    ];

    if (!globalTerritoriesApplied) {
      list.push({
        field: 'territory',
        locale: 'territory',
        type: buildingId || inspectionId ? 'hidden' : FilterTypes.GenericMultiSelect,
        active: filters.active.territory,
        value: filters.values.territory,
        withUnassigned: true,
        onChange: onChangeFilter('territory'),
        onToggle: onToggleFilter('territory')
      });
    }

    list.push({
      field: 'additionalTerritory',
      backendField: 'additional_territory',
      locale: 'additional_territory',
      type: hasPrimaryTerritoryScope ? FilterTypes.GenericMultiSelect : FilterTypes.Hidden,
      active: filters.active.additionalTerritory,
      value: filters.values.additionalTerritory,
      onChange: onChangeFilter('additionalTerritory'),
      onToggle: onToggleFilter('additionalTerritory')
    });

    return list;
  }, [
    filters,
    statusesForSelect,
    usersForSelect,
    proposalTypesForSelect,
    dispatch,
    buildingId,
    accountId,
    inspectionId,
    globalTerritoriesApplied
  ]);

  return filtersList;
};

export default useFilters;
